import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../../services/api/authService';
import './LoginPage.css';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    phoneNumber: '',
    rastId: '',
    email: '',
    deliveryMethod: 'SMS'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    const rastIdFromUrl = searchParams.get('rastId');
    
    if (rastIdFromUrl) {
      // Store in localStorage for persistence
      localStorage.setItem('rastId', rastIdFromUrl);
      setFormData(prev => ({ ...prev, rastId: rastIdFromUrl }));
      
      // Also store in cookies as backup (24 hours)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 1);
      document.cookie = `rastId=${rastIdFromUrl}; expires=${expiryDate.toUTCString()}; path=/`;
    } else {
      // Check if rastId exists in localStorage from previous scan
      const storedRastId = localStorage.getItem('rastId');
      if (storedRastId) {
        setFormData(prev => ({ ...prev, rastId: storedRastId }));
      }
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If rastId is being entered, save it to localStorage immediately
    if (name === 'rastId' && value) {
      localStorage.setItem('rastId', value);
      
      // Also store in cookies as backup (24 hours)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 1);
      document.cookie = `rastId=${value}; expires=${expiryDate.toUTCString()}; path=/`;
    }
    
    setError('');
  };

  const validateForm = () => {
    if (!formData.phoneNumber) {
      setError('Phone number is required');
      return false;
    }
    if (!formData.rastId) {
      setError('Restaurant ID is required. Please scan the QR code.');
      return false;
    }
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setError('Please enter a valid phone number');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    const result = await authService.requestOtp(
      formData.phoneNumber,
      formData.rastId,
      formData.email,
      formData.deliveryMethod
    );

    if (result.success) {
      // OTP feature flag OFF: backend already authenticated us and the token
      // was persisted by authService. Skip the OTP screen entirely.
      if (result.data?.otpRequired === false) {
        setLoggedIn(true);

        setTimeout(() => {
          navigate('/?showNameModal=true', { replace: true });
        }, 1000);

        setLoading(false);
        return;
      }

      // OTP feature flag ON: keep the existing OTP entry + verify flow.
      setSuccess(true);

      sessionStorage.setItem('authData', JSON.stringify({
        phoneNumber: formData.phoneNumber,
        rastId: formData.rastId,
        email: formData.email
      }));

      setTimeout(() => {
        navigate('/verify-otp');
      }, 2000);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">Welcome to Rastaura</h1>
          <p className="login-subtitle">Enter your details to continue</p>
        </div>

        {loggedIn ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>You're signed in!</p>
            <p className="redirect-text">Taking you to the menu...</p>
          </div>
        ) : success ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>OTP sent successfully!</p>
            <p className="redirect-text">Redirecting to verification...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="9987998799"
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email (optional)</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                className="form-input"
                disabled={loading}
              />
              <p className="help-text">
                Add your email to unlock the best offers and discounts.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="rastId">Restaurant ID</label>
              <input
                type="text"
                id="rastId"
                name="rastId"
                value={formData.rastId}
                onChange={handleInputChange}
                placeholder="Scan QR code to auto-fill"
                className="form-input"
                disabled={loading}
              />
              {!formData.rastId && (
                <p className="help-text">Please scan the QR code at your table</p>
              )}
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <span className="loading-text">
                  Signing in
                  <span className="loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </span>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
